import {Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatCard, MatCardContent, MatCardHeader, MatCardTitle} from "@angular/material/card";
import {MatFormField, MatLabel, MatError, MatSuffix} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatButton} from "@angular/material/button";
import {IDropdownOption} from "../../../interfaces/IDropDownOptions";
import {CharityService} from "../../../services/charity.service";
import {ErrorHandlingService} from "../../../services/error-handling.service";
import {IGameQueryResult} from "../../../interfaces/IGameQueryResult";
import {UIStateEnum} from "../../../enum/UIStateEnum";
import {FilterableDropdownComponent} from "../../../components/filterable-dropdown/filterable-dropdown.component";
import {APIService} from "../../../services/api.service";
import {SnackbarService} from "../../../services/snackbar.service";
import {UtilService} from "../../../services/util.service";
import {ICharityBalance} from "../../../interfaces/charity/ICharityBalance";
import {CharityPayoutService} from "../../../services/charity-payout.service";
import {PipesModule} from "../../../pipes/pipes.module";
import {MatIcon} from "@angular/material/icon";
import {NgClass} from "@angular/common";
import {MatTooltip} from "@angular/material/tooltip";
import {Subject, takeUntil} from "rxjs";

@Component({
  selector: 'app-add-charity-payouts',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatCardContent,
    MatFormField,
    MatLabel,
    MatInput,
    MatButton,
    MatError,
    FilterableDropdownComponent,
    PipesModule,
    MatIcon,
    NgClass,
    MatSuffix,
    MatTooltip
  ],
  templateUrl: './add-charity-payouts.component.html',
  styleUrls: ['./add-charity-payouts.component.scss']
})
export class AddCharityPayoutsComponent implements OnInit, OnDestroy {

  private charityService: CharityService = inject(CharityService);
  private charityPayoutService: CharityPayoutService = inject(CharityPayoutService);
  private errorHandlingService: ErrorHandlingService = inject(ErrorHandlingService);
  private apiService: APIService = inject(APIService);
  private snackbarService: SnackbarService = inject(SnackbarService);
  private utilService: UtilService = inject(UtilService);
  private destroy$ = new Subject<void>();

  @Input() set gameData(gameP: IGameQueryResult) {
    // Reset state when game changes
    if (this.activeGame && this.activeGame.Id !== gameP.Id) {
      this.resetPayoutState();
    }
    this.activeGame = gameP;
    this.fetchCharitiesForGame();
  }

  @Input() set preselectedCharityId(charityId: string | null) {
    this._preselectedCharityId = charityId;
    if (charityId) {
      this.onCharitySelected(charityId);
    } else {
      this._preselectedCharityId = null;
    }
  }

  private _preselectedCharityId: string | null = null;

  get selectedCharityId(): string | null {
    return this._preselectedCharityId;
  }

  private resetPayoutState(): void {
    this.payoutForm.reset();
    this.charityBalance = null;
    this.payoutWarning = null;
    this._preselectedCharityId = null;
  }

  @Output() payoutCreated: EventEmitter<void> = new EventEmitter<void>();

  public activeGame: IGameQueryResult | null = null;
  public charityOptions: IDropdownOption[] = [];
  public uiState = UIStateEnum.ShowData;
  public isSubmitting = false;
  public charityBalance: ICharityBalance | null = null;
  public payoutWarning: { message: string; type: 'overpayment' | 'underpayment' } | null = null;

  public payoutForm = new FormGroup({
    charityId: new FormControl('', [Validators.required]),
    payoutAmount: new FormControl<number | null>(null, [Validators.required, Validators.min(0.01)]),
    note: new FormControl<string | null>(null)
  });

  ngOnInit(): void {
    // Subscribe to payout amount changes and recalculate warning
    this.payoutForm.get('payoutAmount')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updatePayoutWarning();
        this.updateNoteValidation();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  fetchCharitiesForGame(): void {
    if (!this.activeGame) return;

    this.uiState = UIStateEnum.ShowLoading;
    this.charityService.fetchCharitiesPerGameByCategory(this.activeGame.Id).subscribe({
      next: (charitiesByCategory) => {
        const allCharities = charitiesByCategory.flatMap(category => category.Charities);
        this.charityOptions = allCharities.map(charity => ({
          id: charity.Id,
          name: charity.Name
        }));
        this.uiState = UIStateEnum.ShowData;
      },
      error: (err) => {
        this.uiState = UIStateEnum.ShowData;
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });
  }

  onCharitySelected(charityId: string): void {
    this.charityBalance = null;
    this.payoutForm.patchValue({ charityId });
    this.fetchCharityBalance(charityId);
  }

  fetchCharityBalance(charityId: string): void {
    this.charityPayoutService.fetchBalanceByCharityId(charityId, this.activeGame?.Id).subscribe({
      next: (balance) => {
        this.charityBalance = balance;
        this.updatePayoutWarning();
        this.updateNoteValidation();
      },
      error: (err) => {
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });
  }

  sanitizeCurrencyInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const rawValue = input.value;
    const cleanedValue = this.utilService.sanitizeCurrencyString(rawValue);
    const numericValue = this.utilService.parseCurrencyValue(rawValue);

    if (numericValue !== null) {
      this.payoutForm.patchValue({ payoutAmount: numericValue }, { emitEvent: false });
      input.value = cleanedValue;
      this.updatePayoutWarning();
      this.updateNoteValidation();
    } else if (cleanedValue === '' || cleanedValue === '.') {
      this.payoutForm.patchValue({ payoutAmount: null }, { emitEvent: false });
      input.value = cleanedValue;
      this.updatePayoutWarning();
      this.updateNoteValidation();
    } else {
      const currentValue = this.payoutForm.get('payoutAmount')?.value;
      input.value = currentValue !== null ? String(currentValue) : '';
    }
  }

  fillFullAmountOwed(): void {
    if (this.charityBalance && this.charityBalance.AmountOwed > 0) {
      const amountInDollars = this.utilService.centsToDollars(this.charityBalance.AmountOwed);
      this.payoutForm.patchValue({ payoutAmount: amountInDollars });
    }
  }

  private updatePayoutWarning(): void {
    if (!this.charityBalance) {
      this.payoutWarning = null;
      return;
    }

    const payoutAmountControl = this.payoutForm.get('payoutAmount');
    if (!payoutAmountControl?.value) {
      this.payoutWarning = null;
      return;
    }

    const payoutAmountInCents = Math.round(payoutAmountControl.value * 100);
    const amountOwed = this.charityBalance.AmountOwed;

    if (payoutAmountInCents > amountOwed) {
      const overageInCents = payoutAmountInCents - amountOwed;
      const overageInDollars = (overageInCents / 100).toFixed(2);
      this.payoutWarning = {
        message: `Warning: This payout exceeds the amount owed by $${overageInDollars}. A note explaining the overpayment is required.`,
        type: 'overpayment'
      };
    } else if (payoutAmountInCents < amountOwed) {
      const shortfallInCents = amountOwed - payoutAmountInCents;
      const shortfallInDollars = (shortfallInCents / 100).toFixed(2);
      this.payoutWarning = {
        message: `Note: This payout is $${shortfallInDollars} less than the amount owed`,
        type: 'underpayment'
      };
    } else {
      this.payoutWarning = null;
    }
  }

  private updateNoteValidation(): void {
    const noteControl = this.payoutForm.get('note');
    if (!noteControl) return;

    // Check if payout amount exceeds amount owed
    if (this.isOverpayment()) {
      // Note is required for overpayments
      noteControl.setValidators([Validators.required]);
    } else {
      // Note is optional for normal or underpayments
      noteControl.clearValidators();
    }

    noteControl.updateValueAndValidity({ emitEvent: false });
  }

  private isOverpayment(): boolean {
    if (!this.charityBalance) return false;

    const payoutAmountControl = this.payoutForm.get('payoutAmount');
    if (!payoutAmountControl?.value) return false;

    const payoutAmountInCents = Math.round(payoutAmountControl.value * 100);
    const amountOwed = this.charityBalance.AmountOwed;

    return payoutAmountInCents > amountOwed;
  }

  onSubmit(): void {
    if (this.payoutForm.invalid || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    const formValue = this.payoutForm.value;

    // Convert dollars to cents (PayoutAmount is in cents on the backend)
    const payoutAmountInCents = Math.round((formValue.payoutAmount || 0) * 100);

    const payload = {
      CharityId: formValue.charityId,
      PayoutAmount: payoutAmountInCents,
      GameId: this.activeGame?.Id,
      Note: formValue.note && formValue.note.trim() ? formValue.note : null
    };

    this.apiService.MakePostRequest<{CharityPayoutId: string}>('charity-payout', payload).subscribe({
      next: () => {
        this.snackbarService.openSuccessfulSnackBar('Charity payout created successfully!');
        this.payoutForm.reset();
        this.charityBalance = null;
        this.payoutCreated.emit();
        this.isSubmitting = false;
      },
      error: (err) => {
        this.errorHandlingService.displayPageLevelErrorMessage(err);
        this.isSubmitting = false;
      }
    });
  }

  protected readonly UIStateEnum = UIStateEnum;
}
